import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useUserData } from '../contexts/UserDataContext';
import { useNavigate } from 'react-router-dom';

const {
  FiTarget,
  FiTrendingUp,
  FiBookOpen,
  FiVideo,
  FiUsers,
  FiEdit3,
  FiCheckCircle,
  FiArrowRight,
  FiArrowLeft,
  FiCalendar
} = FiIcons;

function OnboardingFlow() {
  const navigate = useNavigate();
  const { completeOnboarding, updateUserData } = useUserData();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    primaryGoal: '',
    sport: '',
    graduationYear: '',
    topPriority: ''
  });

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to AthletePath!',
      progress: 0
    },
    {
      id: 'goal',
      title: 'What do you want to accomplish?',
      progress: 25
    },
    {
      id: 'sport',
      title: 'Tell us about your sport',
      progress: 50
    },
    {
      id: 'priority',
      title: "What's your top priority?",
      progress: 75
    }
  ];

  const primaryGoals = [
    {
      id: 'profile',
      icon: FiEdit3,
      title: 'Create My Athlete Profile',
      description: 'Build a professional resume to share with college coaches',
      color: 'blue'
    },
    {
      id: 'timeline',
      icon: FiTrendingUp,
      title: 'Track My Scholarship Journey',
      description: 'Stay organized with milestones and important deadlines',
      color: 'green'
    },
    {
      id: 'schools',
      icon: FiBookOpen,
      title: 'Research College Programs',
      description: 'Find and connect with schools that match my goals',
      color: 'purple'
    },
    {
      id: 'all',
      icon: FiTarget,
      title: 'All of the Above',
      description: "I want the complete toolkit for my scholarship journey",
      color: 'yellow'
    }
  ];

  const topPriorities = [
    {
      id: 'complete-profile',
      icon: FiEdit3,
      title: 'Complete My Profile',
      description: 'Add stats, achievements, and personal information',
      action: 'Go to Settings',
      route: '/settings'
    },
    {
      id: 'create-timeline',
      icon: FiCalendar,
      title: 'Set Up My Timeline',
      description: 'Create milestones for my scholarship journey',
      action: 'Open Timeline',
      route: '/dashboard'
    },
    {
      id: 'find-schools',
      icon: FiBookOpen,
      title: 'Find Target Schools',
      description: 'Research and save colleges to my list',
      action: 'Browse Schools',
      route: '/schools'
    },
    {
      id: 'plan-content',
      icon: FiVideo,
      title: 'Plan My Highlight Videos',
      description: 'Learn how to create compelling recruiting content',
      action: 'Content Hub',
      route: '/content-planning'
    }
  ];

  const popularSports = [
    'Basketball',
    'Football',
    'Soccer',
    'Baseball',
    'Softball',
    'Volleyball',
    'Track & Field',
    'Swimming'
  ];

  const graduationYears = ['2026', '2027', '2028', '2029', '2030', '2031'];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
      yellow: 'bg-yellow-100 text-yellow-600'
    };
    return colors[color] || 'bg-gray-100 text-gray-600';
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    // Save onboarding data
    const onboardingData = {
      primaryGoal: answers.primaryGoal,
      sport: answers.sport,
      graduationYear: answers.graduationYear,
      topPriority: answers.topPriority,
      completedAt: new Date().toISOString()
    };

    // Update profile with basic info
    updateUserData('profile', {
      sport: answers.sport,
      graduationYear: answers.graduationYear
    });

    // Save onboarding responses
    updateUserData('onboardingData', onboardingData);

    // Mark onboarding as complete
    completeOnboarding();

    // Navigate based on top priority
    const priority = topPriorities.find(p => p.id === answers.topPriority);
    if (priority) {
      navigate(priority.route);
    } else {
      navigate('/dashboard');
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return true;
      case 1:
        return answers.primaryGoal !== '';
      case 2:
        return answers.sport !== '' && answers.graduationYear !== '';
      case 3:
        return answers.topPriority !== '';
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiTarget} className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-space-grotesk font-bold text-secondary mb-4">
              Welcome to AthletePath!
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              Your complete platform for managing your college athletic scholarship journey
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="bg-blue-50 rounded-lg p-6 text-left">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={FiEdit3} className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">Public Athlete Profile</h3>
                <p className="text-sm text-gray-600">
                  Create a professional resume showcasing your stats, achievements, and videos
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-6 text-left">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">Track Your Progress</h3>
                <p className="text-sm text-gray-600">
                  Stay organized with customizable timelines and milestone tracking
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-6 text-left">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={FiBookOpen} className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">School Directory</h3>
                <p className="text-sm text-gray-600">
                  Research colleges, save favorites, and manage coach communications
                </p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-6 text-left">
                <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={FiVideo} className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">Content Planning</h3>
                <p className="text-sm text-gray-600">
                  Get guidance on creating highlight videos that impress coaches
                </p>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="max-w-3xl mx-auto">
            <p className="text-gray-600 text-center mb-8">
              Choose what best describes your current focus
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {primaryGoals.map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => setAnswers(prev => ({ ...prev, primaryGoal: goal.id }))}
                  className={`p-6 rounded-xl border-2 transition-all text-left ${
                    answers.primaryGoal === goal.id
                      ? 'border-primary bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getColorClasses(goal.color)}`}>
                      <SafeIcon icon={goal.icon} className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-800">{goal.title}</h3>
                        {answers.primaryGoal === goal.id && (
                          <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{goal.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  What sport do you play? *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {popularSports.map((sport) => (
                    <button
                      key={sport}
                      onClick={() => setAnswers(prev => ({ ...prev, sport }))}
                      className={`px-4 py-3 rounded-lg border-2 transition-all font-medium ${
                        answers.sport === sport
                          ? 'border-primary bg-blue-50 text-primary'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      {sport}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Don't see your sport? You can add it later in settings.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  What year do you graduate? *
                </label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {graduationYears.map((year) => (
                    <button
                      key={year}
                      onClick={() => setAnswers(prev => ({ ...prev, graduationYear: year }))}
                      className={`px-4 py-3 rounded-lg border-2 transition-all font-semibold ${
                        answers.graduationYear === year
                          ? 'border-primary bg-blue-50 text-primary'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mt-6">
                <p className="text-sm text-gray-700">
                  <strong>Timeline Tip:</strong> Most athletes start reaching out to colleges during sophomore or junior year. The earlier you start building your profile and relationships, the better!
                </p>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="max-w-3xl mx-auto">
            <p className="text-gray-600 text-center mb-8">
              Let's get you started on the right foot. What would you like to do first?
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {topPriorities.map((priority) => (
                <button
                  key={priority.id}
                  onClick={() => setAnswers(prev => ({ ...prev, topPriority: priority.id }))}
                  className={`p-6 rounded-xl border-2 transition-all text-left ${
                    answers.topPriority === priority.id
                      ? 'border-primary bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={priority.icon} className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-800">{priority.title}</h3>
                        {answers.topPriority === priority.id && (
                          <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{priority.description}</p>
                      <div className="inline-flex items-center space-x-2 text-xs text-primary font-medium">
                        <span>Next: {priority.action}</span>
                        <SafeIcon icon={FiArrowRight} className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm font-medium text-primary">
              {steps[currentStep].progress}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${steps[currentStep].progress}%` }}
              transition={{ duration: 0.5 }}
              className="bg-primary h-2 rounded-full"
            />
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 mb-8"
          >
            {currentStep > 0 && (
              <div className="mb-8 text-center">
                <h2 className="text-2xl font-space-grotesk font-bold text-secondary mb-2">
                  {steps[currentStep].title}
                </h2>
              </div>
            )}
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2 ${
              currentStep === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={handleNext}
            disabled={!isStepValid()}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2 ${
              isStepValid()
                ? 'bg-primary text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <span>{currentStep === steps.length - 1 ? 'Get Started' : 'Next'}</span>
            <SafeIcon
              icon={currentStep === steps.length - 1 ? FiCheckCircle : FiArrowRight}
              className="w-4 h-4"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export default OnboardingFlow;